import { useRef } from "react";
import {
  motion, useScroll, useTransform, useSpring, useMotionValue, useReducedMotion,
} from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Bug, Zap, Monitor, HardDrive, Cpu, Wifi, ShieldHalf,
  ShieldCheck, Phone, Mail, MapPin, Clock, Lock, Award, FileCheck,
  CheckCircle2, ArrowRight, Star, BadgeCheck, Sparkles, Asterisk,
  Briefcase, Users, Network, FileText, CalendarClock, ReceiptText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ContactForm from "@/components/site/ContactForm";
import TrustpilotReviewCollector from "@/components/site/TrustpilotReviewCollector";
import Seo, { faqJsonLd } from "@/components/site/Seo";
import { BUSINESS, SERVICES, PROCESS_STEPS, TESTIMONIALS, FAQS, CERTIFICATIONS } from "@/lib/siteData";

const ICONS = { Bug, Zap, Monitor, HardDrive, Cpu, Wifi, ShieldHalf };

/* One signature easing curve for the whole page — long, exponential, editorial. */
const EASE = [0.16, 1, 0.3, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.85, delay: i * 0.07, ease: EASE },
  }),
};

/* Kinetic masked-line reveal: text slides up from behind an overflow mask. */
const maskLine = {
  hidden: { y: "115%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.35 + i * 0.14, ease: EASE },
  }),
};

function MaskedLine({ children, i, className = "" }) {
  return (
    <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
      <motion.span
        custom={i}
        initial="hidden"
        animate="show"
        variants={maskLine}
        className={`block will-change-transform ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

function SectionHeading({ index, overline, title, subtitle, align = "left" }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      variants={fadeUp}
      className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}
    >
      <div className={`flex items-center gap-4 ${align === "center" ? "justify-center" : ""}`}>
        {index && (
          <span className="font-heading text-sm font-black tracking-[0.2em] text-primary">
            {index}
          </span>
        )}
        <span className="h-px w-10 bg-primary/50" aria-hidden />
        <p className="text-overline text-primary">{overline}</p>
      </div>
      <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mt-4">
        {title}
      </h2>
      {subtitle && (
        <p className="text-base md:text-lg text-muted-foreground mt-4 leading-relaxed">{subtitle}</p>
      )}
    </motion.div>
  );
}

/* ---------------- HERO ---------------- */
function Hero() {
  const sectionRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const gridY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "9%"]);

  /* Mouse-driven 3D tilt on the clipped photo frame */
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [4.5, -4.5]), { stiffness: 140, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-4.5, 4.5]), { stiffness: 140, damping: 20 });

  const handleMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const handleMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section ref={sectionRef} data-testid="hero" className="relative overflow-hidden border-b border-border">
      <motion.div
        style={reduceMotion ? undefined : { y: gridY }}
        className="absolute inset-0 hero-grid-bg pointer-events-none"
        aria-hidden
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-20 md:pt-24 md:pb-28 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <motion.div initial="hidden" animate="show" variants={fadeUp}>
              <Badge
                data-testid="hero-badge"
                variant="outline"
                className="rounded-full border-border bg-background/70 backdrop-blur px-3 py-1 text-xs font-medium"
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent mr-2" />
                Independent repair shop · Est. {BUSINESS.established}
              </Badge>
            </motion.div>

            <h1
              data-testid="hero-title"
              className="font-heading font-black tracking-tighter leading-[0.96] mt-8 text-[2.75rem] sm:text-6xl lg:text-7xl xl:text-[5.35rem]"
            >
              <MaskedLine i={0}>Slow PC or Mac?</MaskedLine>
              <MaskedLine i={1} className="text-primary">Fixed flat-rate.</MaskedLine>
              <MaskedLine i={2}>No scare tactics.</MaskedLine>
            </h1>

            <motion.p
              initial="hidden" animate="show" custom={4} variants={fadeUp}
              className="text-base md:text-lg text-muted-foreground mt-7 max-w-2xl leading-relaxed"
            >
              An independent <strong className="text-foreground font-semibold">remote-only PC &amp; Mac support service for U.S. customers</strong> — virus &amp; malware removal,
              Windows PC tune-ups and macOS troubleshooting, delivered over secure encrypted sessions.
              Every job begins with a free written diagnostic and a fixed-price quote — clear, upfront
              and in writing, before any work starts.
            </motion.p>

            <motion.div
              initial="hidden" animate="show" custom={5} variants={fadeUp}
              className="mt-9 flex flex-col sm:flex-row gap-3"
            >
              <Button
                asChild
                size="lg"
                data-testid="hero-primary-cta"
                className="bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-6 text-base font-semibold active:scale-[0.98] transition-transform"
              >
                <a href="#contact"><ArrowRight className="h-4 w-4 mr-2" /> Book a free diagnostic</a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                data-testid="hero-secondary-cta"
                className="h-12 px-6 text-base font-semibold border-border hover:bg-secondary"
              >
                <a href={BUSINESS.phoneHref}><Phone className="h-4 w-4 mr-2" /> Call {BUSINESS.phone}</a>
              </Button>
            </motion.div>

            <motion.ul
              initial="hidden" animate="show" custom={6} variants={fadeUp}
              className="mt-9 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-xl"
            >
              {[
                "Flat-rate written quotes",
                "No-fix-no-fee policy",
                "30-day workmanship warranty",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                  <span className="text-foreground/80 leading-snug">{t}</span>
                </li>
              ))}
            </motion.ul>
          </div>

          <motion.div
            initial="hidden" animate="show" custom={7} variants={fadeUp}
            className="lg:col-span-5"
            style={{ perspective: 1200 }}
          >
            <motion.div
              onMouseMove={reduceMotion ? undefined : handleMouseMove}
              onMouseLeave={reduceMotion ? undefined : handleMouseLeave}
              style={reduceMotion ? undefined : { rotateX, rotateY, transformStyle: "preserve-3d" }}
              className="relative"
            >
              {/* Otto Rosso offset block — the clipped frame */}
              <div className="absolute inset-0 translate-x-4 translate-y-4 bg-primary rounded-sm" aria-hidden />
              <motion.div
                initial={{ clipPath: "inset(0 0 100% 0)" }}
                animate={{ clipPath: "inset(0 0 0% 0)" }}
                transition={{ duration: 1.15, delay: 0.55, ease: EASE }}
                className="relative border border-border bg-card overflow-hidden spotlight"
              >
                <div className="overflow-hidden">
                  <motion.img
                    style={reduceMotion ? undefined : { y: imgY }}
                    src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1600&q=70"
                    alt="A laptop, notebook and headphones on a clean desk — remote support workspace"
                    className="w-full h-[360px] md:h-[460px] object-cover scale-[1.14] will-change-transform"
                  />
                </div>
                <div className="grid grid-cols-3 border-t border-border">
                  <div className="p-4 border-r border-border">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Diagnostic</p>
                    <p className="font-heading text-xl font-bold mt-1">$49</p>
                  </div>
                  <div className="p-4 border-r border-border">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Tune-up</p>
                    <p className="font-heading text-xl font-bold mt-1">$79</p>
                  </div>
                  <div className="p-4">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Virus removal</p>
                    <p className="font-heading text-xl font-bold mt-1">$99</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- EDITORIAL MARQUEE ---------------- */
const MARQUEE_ITEMS = [
  "Virus Removal",
  "Flat-Rate Pricing",
  "Mac Cleanup",
  "No Fix, No Fee",
  "Slow-PC Tune-ups",
  "30-Day Warranty",
  "Data Recovery",
  "Remote-Only · USA",
];

function EditorialMarquee() {
  return (
    <section
      data-testid="trust-marquee"
      aria-label="Trust signals"
      className="border-b border-border bg-secondary overflow-hidden"
    >
      <Marquee speed={20} pauseOnHover gradient={false}>
        <div className="flex items-center py-5 md:py-7">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((t, i) => (
            <div key={i} className="flex items-center shrink-0">
              <span
                className={`font-heading text-2xl md:text-4xl font-black uppercase tracking-tight whitespace-nowrap ${
                  i % 2 === 1 ? "text-outline" : "text-foreground/85"
                }`}
              >
                {t}
              </span>
              <Asterisk
                className="h-6 w-6 md:h-7 md:w-7 text-primary mx-7 md:mx-10 shrink-0"
                strokeWidth={2.5}
                aria-hidden
              />
            </div>
          ))}
        </div>
      </Marquee>
    </section>
  );
}

/* ---------------- PROCESS — numbered manifesto chapters ---------------- */
function Process() {
  return (
    <section id="process" data-testid="process-section" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          index="01"
          overline="How it works"
          title="A clear, transparent process — start to finish."
          subtitle="No surprises, no pressure. You see the price before any work begins, and your data never leaves your device."
        />

        <div className="mt-14 border-t border-border">
          {PROCESS_STEPS.map((step, i) => (
            <motion.div
              key={step.n}
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              custom={i} variants={fadeUp}
              data-testid={`process-step-${i}`}
              className="group grid grid-cols-12 gap-x-4 gap-y-3 md:gap-x-8 items-baseline py-8 md:py-10 px-2 md:px-4 border-b border-border hover:bg-secondary/50 transition-colors duration-500"
            >
              <p className="col-span-3 md:col-span-2 font-heading text-5xl md:text-7xl font-black tracking-tighter text-primary/15 group-hover:text-primary transition-colors duration-500">
                {step.n}
              </p>
              <h3 className="col-span-9 md:col-span-4 font-heading text-2xl md:text-3xl font-bold tracking-tight">
                {step.title}
              </h3>
              <p className="col-span-12 md:col-span-6 text-sm md:text-base text-muted-foreground leading-relaxed">
                {step.text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- SERVICES ---------------- */
function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 px-6 md:px-12 bg-secondary border-y border-border">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <SectionHeading
            index="02"
            overline="Our services"
            title="Seven specialisations. Flat prices for every job."
            subtitle="Pick the service that matches your issue, or let us recommend one during your free phone consultation."
          />
          <a href="#contact" className="text-sm font-semibold text-primary inline-flex items-center hover:underline">
            Not sure which? Ask a technician <ArrowRight className="h-4 w-4 ml-1" />
          </a>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 grid-bordered bg-card">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || ShieldCheck;
            const spans = ["lg:col-span-3", "lg:col-span-3", "lg:col-span-2", "lg:col-span-2", "lg:col-span-2", "lg:col-span-3", "lg:col-span-3"];
            const spanClass = spans[i] || "lg:col-span-2";
            return (
              <motion.div
                key={s.key}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
                custom={i} variants={fadeUp}
                data-testid={`service-card-${s.key}`}
                className={`group p-7 hover:bg-secondary/60 transition-colors duration-500 ${spanClass}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0 transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="font-heading text-sm font-bold text-primary tracking-tight whitespace-nowrap">
                    {s.priceLabel}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-semibold tracking-tight mt-5">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.short}</p>
                <ul className="mt-4 space-y-1.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                      <span className="text-foreground/80">{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- PRICING ---------------- */
function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          index="03"
          overline="Transparent pricing"
          title="Every job, one fixed price. Quote first, work second."
          subtitle="Prices shown are typical starting fees for standard residential repairs. You receive a written quote before any chargeable work begins."
        />

        <div className="mt-14 border border-border rounded-md overflow-hidden bg-card">
          <div className="hidden md:grid grid-cols-12 bg-secondary text-overline text-muted-foreground border-b border-border">
            <div className="col-span-5 px-6 py-4">Service</div>
            <div className="col-span-4 px-6 py-4">Includes</div>
            <div className="col-span-2 px-6 py-4">Typical time</div>
            <div className="col-span-1 px-6 py-4 text-right">Price</div>
          </div>
          {SERVICES.map((s, idx) => {
            const time = ["45 min", "60 min", "60 min", "2–4 hrs", "30 min", "30–60 min", "45 min"][idx];
            return (
              <div
                key={s.key}
                data-testid={`pricing-row-${s.key}`}
                className="group grid grid-cols-1 md:grid-cols-12 border-b border-border last:border-b-0 hover:bg-secondary/50 transition-colors duration-300"
              >
                <div className="md:col-span-5 px-6 py-5">
                  <p className="font-heading font-semibold tracking-tight">{s.title}</p>
                  <p className="text-xs text-muted-foreground mt-1 md:hidden">{s.short}</p>
                </div>
                <div className="md:col-span-4 px-6 py-5 text-sm text-muted-foreground">
                  {s.bullets.slice(0, 2).join(" · ")}
                </div>
                <div className="md:col-span-2 px-6 py-5 text-sm text-muted-foreground">{time}</div>
                <div className="md:col-span-1 px-6 py-5 md:text-right">
                  <span className="inline-block font-heading text-base font-bold text-primary transition-transform duration-300 group-hover:-translate-y-0.5">
                    {s.priceLabel}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground mt-6 max-w-3xl">
          * Listed prices are labour-only for standard residential repairs in USD. Replacement parts (where applicable)
          are quoted separately at OEM or equivalent cost. The diagnostic fee is waived when you proceed with a repair
          on the same day. Final pricing is confirmed in your written quote.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Button asChild size="lg" data-testid="pricing-cta" className="bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-6 font-semibold">
            <a href="#contact"><Sparkles className="h-4 w-4 mr-2" /> Get my free written quote</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="h-12 px-6 font-semibold border-border">
            <a href={BUSINESS.phoneHref}><Phone className="h-4 w-4 mr-2" /> Call {BUSINESS.phone}</a>
          </Button>
        </div>
      </div>
    </section>
  );
}

/* ---------------- SMALL BUSINESS ---------------- */
const BUSINESS_FEATURES = [
  {
    icon: CalendarClock,
    title: "Priority response",
    text: "Guaranteed sub-60-minute remote response across the U.S. during operating hours, with encrypted secure-screen sessions for every device.",
  },
  {
    icon: Users,
    title: "Fleet maintenance plans",
    text: "Flat monthly rate per device — proactive patching, security checks and unlimited remote support across your team's laptops and desktops.",
  },
  {
    icon: Network,
    title: "Network, Wi-Fi & email",
    text: "Office Wi-Fi tuning, VPN, printers, Microsoft 365 and Google Workspace issues — fixed end-to-end, not just patched over.",
  },
  {
    icon: Lock,
    title: "Security-first repairs",
    text: "Signed NDAs, encrypted workshop network, GDPR-aligned handling and full audit trail for every device that comes in.",
  },
  {
    icon: ReceiptText,
    title: "Itemised business invoicing",
    text: "Clean, tax-ready invoices with PO references. Net-15 terms available after the first job for incorporated customers.",
  },
  {
    icon: FileText,
    title: "After-hours bookings",
    text: "Evenings and weekends available by appointment so we can work on machines outside your team's productive hours.",
  },
];

function SmallBusiness() {
  return (
    <section
      id="business"
      data-testid="business-section"
      className="py-24 md:py-32 px-6 md:px-12 bg-secondary border-y border-border"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <motion.div
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
            >
              <div className="flex items-center gap-4">
                <span className="font-heading text-sm font-black tracking-[0.2em] text-primary">04</span>
                <span className="h-px w-10 bg-primary/50" aria-hidden />
                <p className="text-overline text-primary inline-flex items-center gap-2">
                  <Briefcase className="h-3.5 w-3.5" /> For small businesses
                </p>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mt-4">
                Keep your team online.<br />
                <span className="text-primary">We&rsquo;ll handle the rest.</span>
              </h2>
              <p className="text-base md:text-lg text-muted-foreground mt-5 leading-relaxed">
                From two-person studios to 50-seat offices, Repair Point is the outsourced remote
                break-fix and security partner small businesses rely on across the United States.
                Predictable pricing, signed agreements, and a single point of contact.
              </p>

              <ul className="mt-7 space-y-3">
                {[
                  "Dedicated account manager",
                  "Per-device or per-incident pricing",
                  "Asset register & repair history kept on file",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-accent shrink-0" />
                    <span className="text-foreground/85">{t}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Button
                  asChild
                  size="lg"
                  data-testid="business-cta"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-6 font-semibold"
                >
                  <a href="#contact"><Briefcase className="h-4 w-4 mr-2" /> Talk to a business advisor</a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="h-12 px-6 font-semibold border-border bg-card hover:bg-card/80"
                >
                  <a href={BUSINESS.phoneHref}><Phone className="h-4 w-4 mr-2" /> Call {BUSINESS.phone}</a>
                </Button>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 grid-bordered bg-card">
              {BUSINESS_FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <motion.div
                    key={f.title}
                    initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
                    custom={i} variants={fadeUp}
                    data-testid={`business-feature-${i}`}
                    className="group p-6 md:p-7 hover:bg-secondary/40 transition-colors duration-500"
                  >
                    <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-heading text-base font-semibold tracking-tight mt-5">{f.title}</h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.text}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- TESTIMONIALS ---------------- */
function Testimonials() {
  return (
    <section data-testid="testimonials-section" className="py-24 md:py-32 px-6 md:px-12 bg-secondary border-y border-border">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          index="05"
          overline="Customer stories"
          title="Honest work, repeat customers."
          subtitle="Real feedback from people we have helped. Names abbreviated for privacy."
        />

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 grid-bordered bg-card">
          {TESTIMONIALS.map((t, i) => (
            <motion.figure
              key={t.name}
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
              custom={i} variants={fadeUp}
              className="p-8 hover:bg-secondary/40 transition-colors duration-500"
            >
              <div className="flex items-center gap-0.5" aria-label="5 out of 5 stars">
                {[...Array(5)].map((_, k) => (
                  <Star key={k} className="h-4 w-4 fill-primary text-primary" />
                ))}
              </div>
              <blockquote className="text-base leading-relaxed text-foreground mt-4">
                &ldquo;{t.text}&rdquo;
              </blockquote>
              <figcaption className="mt-6 pt-6 border-t border-border">
                <p className="font-heading font-semibold tracking-tight">{t.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t.role}</p>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- CERTIFICATIONS ---------------- */
const CERT_ICONS = { gdpr: Lock, "iso-9001": Award, "iso-27001": FileCheck };

function Certifications() {
  return (
    <section id="certifications" data-testid="certifications-section" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          index="06"
          overline="Compliance & certifications"
          title="Independently audited. Built around your privacy."
          subtitle="Our processes are aligned with international standards for quality, information security and data protection — so your device and your data are handled with the same care a regulated workplace would expect."
        />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 grid-bordered bg-card">
          {CERTIFICATIONS.map((c, i) => {
            const Icon = CERT_ICONS[c.key] || ShieldCheck;
            return (
              <motion.div
                key={c.key}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
                custom={i} variants={fadeUp}
                data-testid={`certification-${c.key}`}
                className="group p-7 md:p-8 hover:bg-secondary/50 transition-colors duration-500 flex flex-col"
              >
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0 transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading text-xs font-bold tracking-[0.18em] uppercase text-primary">{c.body}</p>
                    <h3 className="font-heading text-xl font-semibold tracking-tight mt-1">{c.label}</h3>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-5 leading-relaxed">{c.detail}</p>
                <div className="mt-6 pt-5 border-t border-border flex items-center gap-2 text-xs text-muted-foreground">
                  <BadgeCheck className="h-4 w-4 text-accent" />
                  <span>Verified internal control</span>
                </div>
              </motion.div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground mt-8 max-w-3xl leading-relaxed">
          References to ISO 9001:2015 and ISO/IEC 27001:2022 describe the internal management
          standards Repair Point follows. Where formal third-party certificates are required for a
          procurement process, please contact us at{" "}
          <a className="text-primary underline" href={BUSINESS.emailHref}>{BUSINESS.email}</a> for
          our current certificate status and audit documentation.
        </p>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
function FAQ() {
  return (
    <section id="faq" data-testid="faq-section" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeading
            index="07"
            overline="Frequently asked"
            title="Questions, answered plainly."
            subtitle="If you don't see your question here, please call or message us — we'll happily answer before you book anything."
          />
          <div className="mt-8 p-6 border border-border rounded-md bg-secondary">
            <p className="text-overline text-primary">Still unsure?</p>
            <p className="text-sm mt-2 text-foreground/80 leading-relaxed">
              Call <a className="text-primary font-semibold underline" href={BUSINESS.phoneHref}>{BUSINESS.phone}</a>{" "}
              for free advice over the phone. No appointment, no obligation.
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <Accordion type="single" collapsible className="border border-border rounded-md bg-card divide-y divide-border" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b-0 px-2"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="px-4 py-5 text-left font-heading text-base font-semibold hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-5 text-sm text-muted-foreground leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

/* ---------------- CONTACT ---------------- */
function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden py-24 md:py-32 px-6 md:px-12 bg-primary text-primary-foreground">
      <span
        aria-hidden
        className="pointer-events-none select-none absolute -bottom-10 left-0 font-heading font-black uppercase tracking-tighter text-[22vw] leading-none whitespace-nowrap"
        style={{ WebkitTextStroke: "1px hsl(0 0% 100% / 0.10)", color: "transparent" }}
      >
        Repair Point
      </span>
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start relative">
        <div className="lg:col-span-5">
          <p className="text-overline text-primary-foreground/70">Book a repair</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mt-3">
            Let&rsquo;s get your machine running like new.
          </h2>
          <p className="text-base md:text-lg text-primary-foreground/80 mt-5 leading-relaxed max-w-md">
            Submit the form and a senior technician will respond with a free written diagnostic within one business hour
            during opening times. Prefer to talk? Call or email us directly.
          </p>

          <ul className="mt-10 space-y-5 text-sm">
            <li className="flex items-start gap-3">
              <Phone className="h-5 w-5 mt-0.5 text-accent" />
              <div>
                <p className="font-semibold">Phone</p>
                <a href={BUSINESS.phoneHref} className="text-primary-foreground/85 hover:underline">{BUSINESS.phone}</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="h-5 w-5 mt-0.5 text-accent" />
              <div>
                <p className="font-semibold">Email</p>
                <a href={BUSINESS.emailHref} className="text-primary-foreground/85 hover:underline break-all">{BUSINESS.email}</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="h-5 w-5 mt-0.5 text-accent" />
              <div>
                <p className="font-semibold">Registered office</p>
                <p className="text-primary-foreground/85">{BUSINESS.address}</p>
                <p className="text-primary-foreground/60 text-xs mt-1">Remote-only · no walk-ins</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="h-5 w-5 mt-0.5 text-accent" />
              <div>
                <p className="font-semibold">Support hours</p>
                <p className="text-primary-foreground/85">{BUSINESS.hours}</p>
              </div>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-7 text-foreground">
          <ContactForm />
        </div>
      </div>
    </section>
  );
}

/* ---------------- PAGE ---------------- */
export default function Home() {
  return (
    <div data-testid="home-page" className="min-h-screen bg-background text-foreground">
      <Seo
        title="Repair Point — Remote PC & Mac Support for U.S. Customers"
        description="Remote-only Windows PC & Mac repair for U.S. homes and small businesses. Virus removal, slow-PC tune-ups, antivirus setup, data recovery and IT support — delivered over secure encrypted sessions. Flat-rate pricing, 30-day workmanship warranty, no-fix-no-fee."
        path="/"
        jsonLd={faqJsonLd()}
      />
      <Header />
      <main>
        <Hero />
        <EditorialMarquee />
        <Process />
        <Services />
        <Pricing />
        <SmallBusiness />
        <Testimonials />
        <TrustpilotReviewCollector />
        <Certifications />
        <FAQ />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
